/**
 * CteEditableName — inline-editable CTE name. Click → input; Enter / blur
 * commits a `renameCte` edit; Escape reverts. Empty / unchanged value → no-op.
 *
 * Mirrors `FilterEditablePill`'s shape (Enter/blur commit, Escape revert).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove/rename CTE.
 */

import { useEffect, useRef, useState } from 'react';

import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';
import { renameCte } from '../../edit/edits/renameCte';

export interface CteEditableNameProps {
  readonly testId: string;
  readonly currentName: string;
  readonly cteIndex: number;
  readonly cteCount: number;
  readonly editContext: VqeEditContext;
}

export function CteEditableName({
  testId,
  currentName,
  cteIndex,
  cteCount,
  editContext,
}: CteEditableNameProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(currentName);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Mirror upstream changes when not actively editing.
  useEffect(() => {
    if (!editing) setValue(currentName);
  }, [currentName, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commit = (): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed === currentName.trim()) {
      setEditing(false);
      setValue(currentName);
      return;
    }
    setEditing(false);
    // `renameCte` wrapper-validates the new identifier and throws BEFORE the
    // commit on invalid input (P12 §4.2 made `commitEdit` itself non-throwing),
    // so this `.catch` covers only that wrapper reject. Record a commit-feedback
    // failure — the VqeFilterChipBuilder idiom — so the reject surfaces as a
    // toast instead of reaching GlobalErrorListener's crash pipeline, then reset
    // the input so the UI stays consistent.
    void renameCte(cteIndex, trimmed, toCommitCtx(editContext), { cteCount }).catch((error: unknown) => {
      useVqeStore.getState().recordCommitFeedback({
        kind: 'failure',
        at: Date.now(),
        message: error instanceof Error ? error.message : String(error),
      });
      setValue(currentName);
    });
  };

  const cancel = (): void => {
    setEditing(false);
    setValue(currentName);
  };

  if (!editing) {
    return (
      <VqeTooltip content={vqeCopy.cte.rename}>
        <button
          type="button"
          className="font-mono text-sm font-medium hover:underline"
          data-testid={testId}
          onClick={() => setEditing(true)}
        >
          {currentName}
        </button>
      </VqeTooltip>
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      data-testid={`${testId}-input`}
      className="rounded border border-input bg-background px-1.5 py-0.5 font-mono text-sm"
      aria-label="Rename CTE"
    />
  );
}
